import role8MfrsSchema from '../../../../assets/forms/role-210000-mfrs.form.json';
import role8MpersSchema from '../../../../assets/forms/role-210000-mpers.form.json';
import role19MfrsSchema from '../../../../assets/forms/role-610000-mfrs.form.json';
import role19MpersSchema from '../../../../assets/forms/role-610000-mpers.form.json';
import { DEI } from '../../../constants/qnames';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { isEmptyCell } from '../validation-field-key.util';
import { parseFlexibleDate } from '../../../utils/helpers';

/**
 * Shared business rules for Role 8 (Statement of Financial Position).
 *
 * Role 8 is the **highest-stakes Tier 4 sheet** — it is the only sheet whose
 * mount-based status is driven by THREE independent layers stacked on top of
 * each other:
 *
 *   1. Static required-field checks across all four (dimension × period)
 *      columns (mirrors validateMandatoryFields()).
 *   2. JSON-schema-declared cross-sheet equality validations (mirrors
 *      EqualityValidationService.validateAllEqualityRules — column-aware
 *      branch). For role 8 today the equality blocks point at:
 *        - statementOfCashFlowsIndirectMethod (role 18) for cash equivalents
 *        - statementOfFinancialPosition (self) for the Assets ↔
 *          EquityAndLiabilities tie-out
 *   3. The bespoke crossSheetEquityByYear consistency check between role 8's
 *      `:Equity` and role 19's `:Equity:End` (mirrors
 *      EquityReconciliationService.validateCrossSheetEquityByYear). This rule
 *      lives on the role-610000 (SOCIE) schema's `:Equity:End` control AND
 *      requires consecutive financial year dates from filingPrerequisites
 *      before it fires.
 *
 * Both the editor's role8-210000-statement-of-financial-position.component.ts
 * AND role8StatementOfFinancialPositionValidator import from this file so the
 * selection literal, column-visibility logic, and cross-sheet target tables
 * live in exactly one place. If the backend ever renames the
 * accounting-standard enum, only this file changes — the parity-check harness
 * will catch any consumer that drifted.
 *
 * ---- Cross-sheet dependency surface --------------------------------------
 *
 * Role 8 reads three cross-sheet slices for its OWN validation:
 *   1. `filingPrerequisites` — drives column visibility:
 *        - `NatureOfFinancialStatements` ("Consolidated" / "Separate") gates
 *          whether the Consolidated columns are visible at all.
 *        - `DisclosureOfFinancialStatementsPreparationForCurrentSubmission`
 *          ("First time preparation ..." vs "Subsequent preparation ...")
 *          gates whether the PreviousYear columns are visible.
 *        - `CompanyCurrentFinancialYearStartDate` /
 *          `CompanyPreviousFinancialYearEndDate` are read by the
 *          crossSheetEquityByYear gate — the rule only fires when those two
 *          dates form a consecutive (no-gap) financial-year boundary.
 *   2. `statementOfCashFlowsIndirectMethod` (Role 18) — provides the
 *      `ssmt:CashAndBankBalances` end-of-period total Role 8's cash row is
 *      compared against (schema-declared equality block on the cash control).
 *   3. `statementOfChangesInEquity` (Role 19) — provides the SOCIE
 *      `:Equity:End` end-of-period total Role 8's `:Equity` total is
 *      compared against (crossSheetEquityByYear). Role 19 ALSO depends on
 *      Role 8 (per SheetDependencyService), so the cycle is by design — both
 *      sheets publish totals that must agree column-by-column.
 *
 * Role 8 ALSO inherits values FROM Role 9 (sub-classification) via the
 * `linkTo` flag on schema controls — but that inheritance is performed by
 * the editor at form-build time and writes the inherited values directly
 * into role 8's persisted payload. By the time the orchestrator reads
 * sheetData, those linked values are already present, so the pure validator
 * does NOT need to read role 9's slice itself for parity.
 *
 * `accountingStandard` arrives via context.accountingStandard (filingInfo's
 * BasisOfAccountingStandardsAppliedToPrepareFinancialStatements field — same
 * concept exposed under filingInfo for the orchestrator and under
 * filingPrerequisites for the editor's CrossReferenceService).
 */

/**
 * Literal value of
 * filingInfo['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects MPERS. Anything else (including undefined) falls back to
 * MFRS, mirroring the component's `isMFRS ? mfrs-file : mpers-file` ternary
 * which defaults to the MFRS schema whenever the cross-reference resolver
 * does NOT report MFRS. We follow the role 15 / role 20 / role 23 lifts'
 * "Malaysian Private Entities Reporting Standards" plural literal so the
 * cross-sheet lift family stays consistent — see
 * `role15-statement-of-comprehensive-income.rules.ts` for the upstream
 * decision and rationale.
 */
export const ROLE8_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE8_MFRS_SCHEMA_FILE = 'role-210000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE8_MPERS_SCHEMA_FILE = 'role-210000-mpers.form.json';

/**
 * Column visibility (Consolidated-vs-Separate × Current-vs-Previous) is no
 * longer derived by a role-local resolver. The gate descriptors that used to
 * live here (`ROLE8_NATURE_*` / `ROLE8_PREPARATION_*`) now live in the central
 * `dimension-visibility-registry.ts` under
 * `SHEET_KEYS.statementOfFinancialPosition`, and both the pure validator and
 * the mounted editor read them through the shared
 * `resolveDimensionVisibility()` util / `CrossReferenceService.getVisibility()`
 * so the two paths cannot drift.
 */

/**
 * filingPrerequisites date fields used by the crossSheetEquityByYear gate.
 * The gate only fires when current year start === previous year end + 1 day.
 */
export const ROLE8_CURRENT_YEAR_START_FIELD =
  DEI.CompanyCurrentFinancialYearStartDate;
export const ROLE8_PREVIOUS_YEAR_END_FIELD =
  DEI.CompanyPreviousFinancialYearEndDate;

export type Role8Dimension = 'Consolidated' | 'Separate';
export type Role8Period = 'CurrentYear' | 'PreviousYear';

/** Narrow shape of a role 8 schema control used by the pure validator. */
export interface Role8EqualityRule {
  sheet: string;
  field: string;
  operation?: 'sum' | string;
  message?: string;
  tolerance?: number;
  taxonomy?: string;
  skipIfSourceNull?: boolean;
  skipIfTargetNull?: boolean;
}

export interface Role8SchemaControl {
  qname: string;
  label?: string;
  type?: string;
  required?: boolean;
  ui?: {
    controlType?: string;
    control?: string;
    dualColumn?: boolean;
    readonly?: boolean;
    calculated?: boolean;
    linkTo?: { role: string; qname: string };
  };
  validation?: {
    dataType?: string;
    equality?: Role8EqualityRule[];
    crossSheetEquityByYear?: {
      compareField: string;
      sheet: string;
      tolerance?: number;
      taxonomy?: string;
    };
  };
}

export interface Role8Schema {
  role?: string;
  roleId?: string;
  taxonomy?: string;
  sheetId?: string;
  label?: string;
  controls: Role8SchemaControl[];
}

/**
 * Resolve the correct role 8 schema filename for the given accounting
 * standard. Mirrors the component's loadSchema() ternary so both paths
 * derive from the same rule.
 */
export function pickRole8SchemaFile(
  accountingStandard: string | undefined | null
): string {
  if (accountingStandard === ROLE8_MPERS_ACCOUNTING_STANDARD) {
    return ROLE8_MPERS_SCHEMA_FILE;
  }
  return ROLE8_MFRS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 8 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-time
 * (the listing/dashboard's cache-miss path runs synchronously) so both
 * variants are imported eagerly and selected from memory.
 */
export function pickRole8Schema(
  accountingStandard: string | undefined | null
): Role8Schema {
  return pickSchema(
    accountingStandard,
    role8MfrsSchema as unknown as Role8Schema,
    role8MpersSchema as unknown as Role8Schema
  );
}

/**
 * Resolve the SOCIE (role 19, role-610000) schema for the given accounting
 * standard. The validator imports this directly so it can replay the
 * editor's `validateCrossSheetEquityByYear` chain without the async
 * FormSchemaService.loadSchema() step.
 */
export function pickRole19Schema(
  accountingStandard: string | undefined | null
): Role8Schema {
  return pickSchema(
    accountingStandard,
    role19MfrsSchema as unknown as Role8Schema,
    role19MpersSchema as unknown as Role8Schema
  );
}

/** Returns true when the accounting standard resolves to MFRS. */
export function isRole8MFRS(
  accountingStandard: string | undefined | null
): boolean {
  return accountingStandard !== ROLE8_MPERS_ACCOUNTING_STANDARD;
}

/** Map the accounting-standard literal to the equivalent equality taxonomy. */
export function role8AccountingStandardToTaxonomy(
  accountingStandard: string | undefined | null
): 'MFRS' | 'MPERS' {
  return accountingStandard === ROLE8_MPERS_ACCOUNTING_STANDARD
    ? 'MPERS'
    : 'MFRS';
}

/** Extract MFRS|MPERS from the schema's `taxonomy` field (e.g. "FS-MFRS"). */
export function extractRole8SchemaTaxonomy(
  schema: Role8Schema
): 'MFRS' | 'MPERS' | null {
  const taxonomy = schema.taxonomy;
  if (!taxonomy) return null;
  if (taxonomy.includes('MFRS')) return 'MFRS';
  if (taxonomy.includes('MPERS')) return 'MPERS';
  return null;
}

/**
 * Build the bracket-notation form key the component uses for a row+column
 * cell: `qname[Consolidated|Separate][CurrentYear|PreviousYear]`. Mirrors
 * the component's getXbrlControlName() helper and validation-field-key.util's
 * `dualColumn` branch in buildFieldKey().
 */
export function buildRole8ColumnKey(
  qname: string,
  dimension: Role8Dimension,
  period: Role8Period
): string {
  return `${qname}[${dimension}][${period}]`;
}

/**
 * Build the SOCIE (equityColumns) key the editor stores Role 19's
 * end-of-period equity totals under. Mirrors validation-field-key.util's
 * `equityColumns` branch in buildFieldKey() and the crossSheetEquityByYear
 * key construction:
 *
 *     `${fieldQname}:${EquityMember}[${dimension}][${period}]`
 */
export function buildRole19EquityKey(
  qname: string,
  dimension: Role8Dimension,
  period: Role8Period,
  accountingStandard: string | undefined | null
): string {
  const equityMember = isRole8MFRS(accountingStandard)
    ? 'ifrs-full:EquityMember'
    : 'ifrs-smes:EquityMember';
  return `${qname}:${equityMember}[${dimension}][${period}]`;
}

/**
 * Empty-value check matching the component's null/undefined/'' guards.
 * The editor's validateMandatoryFields treats only the literal `null` as
 * missing (zero is a valid monetary value); we broaden to undefined / ''
 * to tolerate orchestrator payloads where the cell was never persisted —
 * same posture as roles 9, 15, 20, 23.
 */
export function isRole8Empty(value: unknown): boolean {
  return isEmptyCell(value);
}

/** Header detection — mirrors the component's section-header skip. */
export function isRole8SectionHeader(control: Role8SchemaControl): boolean {
  return control.type === 'SectionHeader';
}

/** Monetary item detection — mirrors the component's isMonetaryItem(). */
export function isRole8MonetaryItem(control: Role8SchemaControl): boolean {
  return control.type === 'MonetaryItemType';
}

/** Calculated-or-readonly check — mirrors the component's isCalculatedField(). */
export function isRole8CalculatedField(control: Role8SchemaControl): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/**
 * Determine whether the financial year boundary is consecutive enough to
 * trigger the crossSheetEquityByYear rule. Mirrors
 * `validateCrossSheetEquityByYear`'s consecutive-date check exactly:
 * current year start === previous year end + 1 day.
 *
 * Returns false when either date is missing or unparseable — matching the
 * editor's silent-skip posture.
 */
export function areRole8YearsConsecutive(
  filingPrerequisites: Record<string, unknown> | undefined
): boolean {
  const startStr = filingPrerequisites?.[ROLE8_CURRENT_YEAR_START_FIELD] as
    | string
    | undefined;
  const endStr = filingPrerequisites?.[ROLE8_PREVIOUS_YEAR_END_FIELD] as
    | string
    | undefined;
  if (!startStr || !endStr) return false;

  // parseFlexibleDate — DEI dates are DD/MM/YYYY; native new Date() misreads
  // slash-dates as US MM/DD/YYYY → Invalid Date → false "not consecutive".
  const startDate = parseFlexibleDate(startStr);
  const endDate = parseFlexibleDate(endStr);
  if (!startDate || !endDate) {
    return false;
  }

  const dayAfterEnd = new Date(endDate);
  dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);
  return startDate.getTime() === dayAfterEnd.getTime();
}

/**
 * Cross-sheet target descriptor for the SOCIE Equity:End comparison. The
 * SFP-side qname comes from
 * `crossSheetEquityByYear.compareField` on the SOCIE schema; the SOCIE-side
 * qname is the SOCIE control that carries the rule. Selection by taxonomy
 * mirrors `validateCrossSheetEquityByYear`'s hard-coded taxonomy ternary.
 */
export interface Role8EquityCrossSheetTargets {
  sfpEquityQname: string;
  socieEquityEndQname: string;
}

export function getRole8EquityCrossSheetTargets(
  accountingStandard: string | undefined | null
): Role8EquityCrossSheetTargets {
  const mfrs = isRole8MFRS(accountingStandard);
  return {
    sfpEquityQname: mfrs ? 'ifrs-full:Equity' : 'ifrs-smes:Equity',
    socieEquityEndQname: mfrs
      ? 'ifrs-full:Equity:End'
      : 'ifrs-smes:Equity:End',
  };
}
